import type { StaffListItem } from '@/definitions/staff'

import { staffDisplayName } from './staffLabels'

/**
 * What the action available on a staff member is called.
 *
 * Deactivate and Reactivate rather than the endpoints' own disable and enable, so
 * the action reads as the inverse of the Active / Deactivated status sitting
 * beside it on the card.
 */
export const staffEnabledActionLabel = (isEnabled: boolean): string =>
  isEnabled ? 'Deactivate' : 'Reactivate'

export interface StaffDeactivationCopy {
  title: string
  /** What deactivating takes away, and when. */
  effect: string
  /** Present only when this is the organization's last administrator with access. */
  lastAdminNote?: string
  /** That it can be given back, which is what keeps the warning proportionate. */
  reversal: string
}

/**
 * The question asked before a staff member is deactivated.
 *
 * Worded as losing the ability to sign in rather than as being signed out:
 * Keycloak turns down their next sign-in and their session's next refresh, but an
 * access token already issued keeps working until it runs out.
 *
 * The last administrator is named rather than protected — deactivating them is
 * allowed, and the operator is the one who knows whether it is wanted, so the cost
 * is stated the same way the rest of the copy states it.
 *
 * Reactivating is not asked about — it hands access back rather than taking it
 * away, and this tab is where it is handed back.
 */
export const staffDeactivationCopy = (
  staff: StaffListItem,
  isLastActiveAdmin: boolean
): StaffDeactivationCopy => ({
  title: `Deactivate ${staffDisplayName(staff)}?`,
  effect:
    `${staffDisplayName(staff)} will not be able to sign in to Barti. If they are ` +
    'signed in now, they lose access when their session next refreshes.',
  lastAdminNote: isLastActiveAdmin
    ? 'They are this organization’s only administrator with access, so it will be ' +
      'left with nobody who can manage its own staff in Bartimaeus.'
    : undefined,
  reversal: 'Reactivating them from this tab gives them back the login they had.',
})

/** The line confirming a change that went through, named so the toast is about a person. */
export const staffEnabledSuccessMessage = (
  staff: StaffListItem,
  isEnabled: boolean
): string =>
  isEnabled
    ? `${staffDisplayName(staff)} can sign in to Barti again.`
    : `${staffDisplayName(staff)} can no longer sign in to Barti.`
